/**
 * Device-local lock for the finance module.
 *
 * This is a second layer on top of the application sign-in, in the spirit of an
 * app lock on a phone: it keeps the numbers off the screen when the dashboard
 * is already open and someone else is looking. It is not encryption — the
 * entries themselves sit in this browser's storage — so the real access control
 * remains the server-side login.
 *
 * There is deliberately no default and no reset-to-default: a shipped product
 * with a known code in its source has no lock at all.
 */

export const PASS_KEY = 'rdi_finance_pass_v3';
export const UNLOCK_KEY = 'rdi_finance_unlocked';
const LEGACY_PASS_KEY = 'rdi_finance_pass_v2';

const ITERATIONS = 20000;

function randomSalt() {
  try {
    const b = new Uint8Array(8);
    (window.crypto || window.msCrypto).getRandomValues(b);
    return Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
  } catch (_) {
    return Math.random().toString(16).slice(2, 18);
  }
}

/**
 * Salted, iterated hash. Synchronous so the lock screen stays simple, and
 * strong enough that the stored value is not a lookup away from the code.
 */
function digest(pwd, salt) {
  let h1 = 0x811c9dc5;
  let h2 = 0x01000193;
  const s = `${salt}:${String(pwd || '')}`;
  for (let round = 0; round < ITERATIONS; round++) {
    for (let i = 0; i < s.length; i++) {
      const c = s.charCodeAt(i) + round;
      h1 = Math.imul(h1 ^ c, 0x01000193) >>> 0;
      h2 = Math.imul(h2 + c, 0x85ebca6b) >>> 0;
      h2 = ((h2 << 13) | (h2 >>> 19)) >>> 0;
    }
  }
  return `${(h1 >>> 0).toString(16)}${(h2 >>> 0).toString(16)}`;
}

function hashWithSalt(pwd, salt) {
  return `v3$${salt}$${digest(pwd, salt)}`;
}

export function getStoredHash() {
  try { return localStorage.getItem(PASS_KEY); } catch (_) { return null; }
}

/** True once the owner has chosen a code on this device. */
export function hasPassword() {
  return !!getStoredHash();
}

export function verifyPassword(pwd, storedHash) {
  if (!storedHash) return false;
  const parts = String(storedHash).split('$');
  if (parts.length !== 3 || parts[0] !== 'v3') return false;
  return hashWithSalt(pwd, parts[1]) === storedHash;
}

export function savePassword(newPwd) {
  try {
    localStorage.setItem(PASS_KEY, hashWithSalt(newPwd, randomSalt()));
    localStorage.removeItem(LEGACY_PASS_KEY);
  } catch (_) { /* private mode — the lock lasts this session only */ }
  setUnlocked(true);
}

export function isUnlocked() {
  try { return sessionStorage.getItem(UNLOCK_KEY) === '1'; } catch (_) { return false; }
}

export function setUnlocked(value) {
  try {
    if (value) sessionStorage.setItem(UNLOCK_KEY, '1');
    else sessionStorage.removeItem(UNLOCK_KEY);
  } catch (_) { /* ignore */ }
}

/**
 * Forgotten code. The code is not an encryption key, so keeping the entries
 * while dropping the lock would just be a back door — the data goes too. The
 * module's Back up file is how a forgotten code is recovered from.
 */
export function clearVault() {
  try {
    localStorage.removeItem(PASS_KEY);
    localStorage.removeItem(LEGACY_PASS_KEY);
    localStorage.removeItem('rdi_finance_v2');
    localStorage.removeItem('rdi_finance_v1');
  } catch (_) { /* ignore */ }
  setUnlocked(false);
}
